import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  Pencil, 
  Trash2, 
  Image as ImageIcon, 
  Bell, 
  User, 
  Users,
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';

// --- Interfaces ---

interface AlertMobile {
  id: string;
  image_url: string;
  action_url: string;
  target_type: 'ALL' | 'SPECIFIC';
  target_user_email: string | null;
  is_active: boolean;
  created_at?: string;
}

// --- Constants ---

const defaultAlertData: Omit<AlertMobile, 'id' | 'created_at'> = {
  image_url: '',
  action_url: '',
  target_type: 'ALL',
  target_user_email: '',
  is_active: true
};

// --- Main Component ---

export const MobileBannersXaigooSection: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertMobile[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Alert States
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<AlertMobile | null>(null);
  const [alertFormData, setAlertFormData] = useState(defaultAlertData);
  
  // Upload States
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('alerts_mobile')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAlerts(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar alertas:', err);
      setMessage(`❌ Erro ao carregar alertas: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `xaigoo-alerts/${fileName}`;

      const { error } = await supabase.storage
        .from('mobile-banners')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('mobile-banners')
        .getPublicUrl(filePath);

      return publicUrl;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setMessage('❌ Selecione apenas arquivos de imagem.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage('❌ A imagem deve ter no máximo 5MB.');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleAlertSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      let finalImageUrl = alertFormData.image_url;
      if (selectedImage) {
        finalImageUrl = await uploadImage(selectedImage);
      }

      const payload = {
        image_url: finalImageUrl,
        action_url: alertFormData.action_url,
        target_type: alertFormData.target_type,
        target_user_email: alertFormData.target_type === 'SPECIFIC' ? alertFormData.target_user_email : null,
        is_active: alertFormData.is_active
      };

      if (editingAlert) {
        const { error } = await supabase.from('alerts_mobile').update(payload).eq('id', editingAlert.id);
        if (error) throw error;
        setMessage('✅ Alerta atualizado!');
      } else {
        const { error } = await supabase.from('alerts_mobile').insert([payload]);
        if (error) throw error;
        setMessage('✅ Alerta disparado com sucesso!');
      }

      closeAlertModal();
      loadAlerts();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setMessage(`❌ Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertDelete = async (id: string) => {
    if (!confirm('Deseja realmente excluir este alerta?')) return;
    try {
      const { error } = await supabase.from('alerts_mobile').delete().eq('id', id);
      if (error) throw error;
      setMessage('✅ Alerta removido!');
      loadAlerts();
    } catch (err: any) {
      setMessage(`❌ Erro: ${err.message}`);
    }
  };

  const toggleAlertStatus = async (alert: AlertMobile) => {
    try {
      const { error } = await supabase
        .from('alerts_mobile')
        .update({ is_active: !alert.is_active })
        .eq('id', alert.id);
      if (error) throw error;
      loadAlerts();
    } catch (err: any) {
      setMessage(`❌ Erro ao alternar status: ${err.message}`);
    }
  };

  const closeAlertModal = () => {
    setIsAlertModalOpen(false);
    setEditingAlert(null);
    setAlertFormData(defaultAlertData);
    setImagePreview(null);
    setSelectedImage(null);
  };

  const openAlertEdit = (alert: AlertMobile) => {
    setEditingAlert(alert);
    setAlertFormData({
      image_url: alert.image_url,
      action_url: alert.action_url || '',
      target_type: alert.target_type,
      target_user_email: alert.target_user_email || '',
      is_active: alert.is_active
    });
    setImagePreview(alert.image_url);
    setIsAlertModalOpen(true);
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-8">
      <div className="p-6">
        {message && (
          <div className={`p-4 rounded-lg text-sm mb-6 flex items-center gap-2 ${
            message.includes('✅') 
            ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
            : 'bg-rose-50 text-rose-800 border-rose-200'
          } border animate-in fade-in duration-300`}>
            {message}
          </div>
        )}

        {/* ALERTS SECTION */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-xl font-bold text-slate-800">Alertas Pop-up Central</h3>
            <p className="text-sm text-slate-500">Gerencie os disparos de banners de alerta que aparecem no centro da tela.</p>
          </div>
          <button
            onClick={() => { closeAlertModal(); setIsAlertModalOpen(true); }}
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 text-sm font-bold transition-all shadow-md active:scale-95"
          >
            <Bell className="w-4 h-4" />
            Novo Alerta Push
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {alerts.map(alert => (
            <div key={alert.id} className={`group bg-white border-2 rounded-2xl overflow-hidden transition-all hover:shadow-xl ${alert.is_active ? 'border-transparent shadow-sm' : 'border-slate-100 opacity-60'}`}>
              <div className="aspect-[4/5] bg-slate-100 relative overflow-hidden">
                <img src={alert.image_url} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button onClick={() => openAlertEdit(alert)} className="bg-white p-3 rounded-full text-blue-600 shadow-xl hover:scale-110 transition-transform"><Pencil className="w-5 h-5" /></button>
                  <button onClick={() => handleAlertDelete(alert.id)} className="bg-white p-3 rounded-full text-rose-600 shadow-xl hover:scale-110 transition-transform"><Trash2 className="w-5 h-5" /></button>
                </div>
                {alert.target_type === 'SPECIFIC' && (
                  <div className="absolute top-3 left-3 bg-purple-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow-lg flex items-center gap-1 uppercase">
                    <User className="w-3 h-3" /> Unitário
                  </div>
                )}
                {alert.target_type === 'ALL' && (
                  <div className="absolute top-3 left-3 bg-blue-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow-lg flex items-center gap-1 uppercase">
                    <Users className="w-3 h-3" /> Global
                  </div>
                )}
              </div>
              <div className="p-4 flex items-center justify-between bg-white border-t">
                <div className="min-w-0">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Status do Alerta</p>
                  <button 
                    onClick={() => toggleAlertStatus(alert)}
                    className={`text-xs font-bold flex items-center gap-1.5 px-2 py-1 rounded-full ${alert.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-50 text-slate-400'}`}
                  >
                    {alert.is_active ? <><Eye className="w-3.5 h-3.5"/> ATIVO</> : <><EyeOff className="w-3.5 h-3.5"/> PAUSADO</>}
                  </button>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter mb-1">Ação</p>
                  <a href={alert.action_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 flex items-center gap-1 text-xs font-bold justify-end">
                    LINK <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          ))}
          {alerts.length === 0 && !loading && (
            <div className="col-span-full py-16 text-center text-slate-400 border-2 border-dashed rounded-3xl bg-slate-50/50">
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="font-bold">Nenhum alerta de push configurado.</p>
              <p className="text-sm">Clique em "Novo Alerta Push" para começar.</p>
            </div>
          )}
        </div>
      </div>

      {/* --- ALERT MODAL --- */}
      {isAlertModalOpen && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white/95 backdrop-blur-md z-10">
              <h3 className="text-xl font-black text-orange-600 italic uppercase flex items-center gap-2">
                <Bell className="w-6 h-6" />
                {editingAlert ? 'Editar Alerta Push' : 'Novo Alerta Push'}
              </h3>
              <button onClick={closeAlertModal} className="text-slate-400 hover:text-slate-900 w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors">✕</button>
            </div>
            <form onSubmit={handleAlertSubmit} className="p-8 space-y-8">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest">Banner Retrato (4:5)</label>
                  <div 
                    className="w-full aspect-[4/5] rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer hover:border-orange-300 hover:bg-orange-50/30 transition-all shadow-inner"
                    onClick={() => document.getElementById('alert-upload')?.click()}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-6 text-slate-400">
                        <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="text-[10px] font-black uppercase tracking-tighter">Clique para subir imagem</p>
                        <p className="text-[9px] mt-1 italic">Proporção ideal: 4:5</p>
                      </div>
                    )}
                    {uploadingImage && <div className="absolute inset-0 bg-white/80 flex items-center justify-center font-bold text-orange-600 animate-pulse uppercase text-xs">Subindo...</div>}
                  </div>
                  <input id="alert-upload" type="file" className="hidden" accept="image/*" onChange={handleImageSelect} />
                </div>

                <div className="space-y-6">
                  <div className="space-y-3">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest">Audiência</label>
                    <div className="grid grid-cols-1 gap-2.5">
                      <button 
                        type="button" 
                        onClick={() => setAlertFormData({...alertFormData, target_type: 'ALL'})}
                        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all font-bold text-sm ${alertFormData.target_type === 'ALL' ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
                      >
                        <Users className="w-4 h-4"/> TODOS
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setAlertFormData({...alertFormData, target_type: 'SPECIFIC'})}
                        className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all font-bold text-sm ${alertFormData.target_type === 'SPECIFIC' ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-md' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
                      >
                        <User className="w-4 h-4"/> ESPECÍFICO
                      </button>
                    </div>
                  </div>

                  {alertFormData.target_type === 'SPECIFIC' && (
                    <div className="space-y-2 animate-in slide-in-from-top-4 duration-300">
                      <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest">E-mail do Alvo</label>
                      <input 
                        type="email" 
                        placeholder="usuario@email.com" 
                        className="w-full px-4 py-3.5 bg-slate-50 border-2 border-purple-100 rounded-2xl text-sm font-bold focus:border-purple-500 outline-none transition-colors" 
                        value={alertFormData.target_user_email || ''} 
                        onChange={e => setAlertFormData({...alertFormData, target_user_email: e.target.value})}
                        required
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest">Link de Ação</label>
                    <input 
                      type="url" 
                      placeholder="https://..." 
                      className="w-full px-4 py-3.5 bg-slate-50 border rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-orange-500 transition-all shadow-inner" 
                      value={alertFormData.action_url} 
                      onChange={e => setAlertFormData({...alertFormData, action_url: e.target.value})}
                      required
                    />
                    <p className="text-[10px] text-slate-400 italic font-medium px-1">O App redirecionará para este link ao clicar.</p>
                  </div>

                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                    <span className="text-xs font-black text-slate-600 uppercase tracking-widest">Situação Ativa</span>
                    <button type="button" onClick={() => setAlertFormData({...alertFormData, is_active: !alertFormData.is_active})} className={`w-12 h-6 rounded-full transition-all relative ${alertFormData.is_active ? 'bg-orange-500 shadow-orange-200 shadow-md' : 'bg-slate-300'}`}>
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${alertFormData.is_active ? 'left-7' : 'left-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-slate-100">
                <button type="submit" disabled={loading || uploadingImage} className="flex-1 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white py-4 rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl shadow-orange-100 disabled:from-slate-400 disabled:to-slate-400 transition-all active:scale-[0.98]">
                  {loading ? 'Processando...' : (editingAlert ? 'Salvar Alterações' : 'Disparar Alerta Agora')}
                </button>
                <button type="button" onClick={closeAlertModal} className="px-8 bg-slate-100 hover:bg-slate-200 text-slate-600 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
